import { FunctionComponent, useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
	EMAIL_DELIVERABILITY_FEATURE_AVAILABILITY,
	EmailDeliverabilityFeatureAvailability,
	hasAnyEmailDeliverabilityFeature,
	RISKY_EMAIL_POLICIES,
} from "@signalist/shared-types";

import { AutoCompleteItem as DropdownAutoCompleteItem } from "@/components/Dropdown/Dropdown";
import { ListSourceType } from "@/enum/list.enum";
import { useGetContactSettingsQuery } from "@/pages/Private/redux/contact/contact.api";
import { contactSelector } from "@/pages/Private/redux/contact/contact.slice";
import { useGetListOutreachConfigQuery } from "@/pages/Private/redux/list/list-outreach.api";
import { listOutreachConfigSelector } from "@/pages/Private/redux/list/list-outreach.slice";
import { useAppSelector } from "@/redux/hooks";

import { listCampaignsEnabled } from "../../../components/ReachOut/reachOutCampaignsEnabled";
import { ListEmailOverridesState } from "../hooks/useListEmailDeliverabilityOverrides";
import {
	riskyPolicyValueOf,
	VERIFICATION_OFF_ID,
	VERIFICATION_ON_ID,
	verificationOptionIdOf,
	verificationValueOf,
} from "../utils/listEmailDeliverability.utils";
import { CollapsibleSettingsSection } from "./CollapsibleSettingsSection";
import { EmailOverrideRow } from "./EmailOverrideRow";
import { EmailWorkspaceRows } from "./EmailWorkspaceRows";

interface ListEditSettingEmailDeliverabilityProps {
	listId: number;
	sourceType: ListSourceType;
	overrides: ListEmailOverridesState;
	/** Which deliverability features have shipped; the shared-types constants unless a spec overrides. */
	featureAvailability?: EmailDeliverabilityFeatureAvailability;
}

/**
 * The "Email deliverability" section of List Settings (PREC-4261), between Job Change Settings and
 * Advanced settings. Nothing renders, and nothing is fetched, while no deliverability feature has
 * shipped: an empty section is a control that names nothing. Each row renders with its own slice's
 * constant.
 */
export const ListEditSettingEmailDeliverability: FunctionComponent<
	ListEditSettingEmailDeliverabilityProps
> = ({ featureAvailability = EMAIL_DELIVERABILITY_FEATURE_AVAILABILITY, ...props }) =>
	hasAnyEmailDeliverabilityFeature(featureAvailability) ? (
		<EmailDeliverabilitySection {...props} featureAvailability={featureAvailability} />
	) : null;

/**
 * Shown only on a list that can run a campaign: the same `listCampaignsEnabled` decision (the
 * list's stored outreach configuration, its source-type default until that has loaded) that gates
 * the Outreach step's campaign actions.
 */
const EmailDeliverabilitySection: FunctionComponent<
	Required<ListEditSettingEmailDeliverabilityProps>
> = ({ listId, sourceType, overrides, featureAvailability }) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`contacts.settings.listDeliverability.${key}`);

	useGetListOutreachConfigQuery(listId);
	const outreachConfig = useAppSelector(listOutreachConfigSelector(listId));

	useGetContactSettingsQuery();
	const { settings } = useAppSelector(contactSelector);

	const verificationChoices = useMemo<DropdownAutoCompleteItem[]>(
		() => [
			{ id: VERIFICATION_ON_ID, title: t("contacts.settings.listDeliverability.on") },
			{ id: VERIFICATION_OFF_ID, title: t("contacts.settings.listDeliverability.off") },
		],
		[t]
	);
	const riskyPolicyChoices = useMemo<DropdownAutoCompleteItem[]>(
		() =>
			RISKY_EMAIL_POLICIES.map((policy) => ({
				id: policy,
				title: t(`contacts.settings.riskyEmails.policy.${policy}`),
			})),
		[t]
	);

	if (!listCampaignsEnabled(sourceType, outreachConfig)) {
		return null;
	}

	return (
		<CollapsibleSettingsSection description={ts("description")} title={ts("title")}>
			<div className="flex flex-col gap-6 w-full" data-testid="list-email-deliverability">
				{featureAvailability.verification && (
					<EmailOverrideRow
						choices={verificationChoices}
						description={ts("verificationDescription")}
						selectedId={verificationOptionIdOf(overrides.emailVerificationEnabled)}
						testId="list-email-verification"
						title={ts("verificationTitle")}
						workspaceId={
							settings
								? (verificationOptionIdOf(settings.emailVerificationEnabled) ?? undefined)
								: undefined
						}
						onChange={(id) => overrides.setEmailVerificationEnabled(verificationValueOf(id))}
					/>
				)}
				{featureAvailability.riskyEmailPolicy && (
					<EmailOverrideRow
						choices={riskyPolicyChoices}
						description={ts("riskyPolicyDescription")}
						selectedId={overrides.riskyEmailPolicy}
						testId="list-risky-email-policy"
						title={ts("riskyPolicyTitle")}
						workspaceId={settings?.riskyEmailPolicy}
						onChange={(id) => overrides.setRiskyEmailPolicy(riskyPolicyValueOf(id))}
					/>
				)}
				<EmailWorkspaceRows
					bounceRepair={
						settings
							? {
									enabled: settings.bounceRepairEnabled,
									reviewOnCompanyChange: settings.bounceRepairReviewOnCompanyChange,
								}
							: undefined
					}
					showBounceRepair={featureAvailability.bounceRepair}
					showSuppression={featureAvailability.suppression}
				/>
			</div>
		</CollapsibleSettingsSection>
	);
};
